import { ReactNode } from "react";
import type { Option } from "./EntityForm";

export interface SelectFilter {
    /** Key in the filter state object. */
    name: string;
    /** Shown as the "all" option, e.g. "All Status". */
    allLabel: string;
    options: Option[];
}

interface Props {
    search: string;
    onSearch: (value: string) => void;
    searchPlaceholder?: string;
    filters?: SelectFilter[];
    values?: Record<string, string>;
    onChange?: (name: string, value: string) => void;
    /** Primary action, e.g. "+ Add New Participant". */
    action?: ReactNode;
    /** Count line shown under the bar when a filter is narrowing the list. */
    resultNote?: string;
}

/**
 * Search, dropdown filters and the primary action, laid out as one row.
 *
 * Filtering is client-side against the already-loaded collection. That is the
 * right call at this data size — every list here is capped at 500 rows by the
 * API — and it keeps the filters instant. If any collection outgrows that, the
 * entity router already accepts the same names as query parameters, so moving a
 * filter server-side is a change to where it is applied, not what it means.
 */
export const FilterBar = ({
    search, onSearch, searchPlaceholder = "Search…",
    filters = [], values = {}, onChange, action, resultNote,
}: Props) => (
    <div className="filterbar">
        <div className="filterbar-row">
            <div className="search">
                <span className="search-icon" aria-hidden="true">⌕</span>
                <input
                    type="search"
                    value={search}
                    onChange={(e) => onSearch(e.target.value)}
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                />
            </div>

            {filters.map((filter) => (
                <select
                    key={filter.name}
                    className="filter-select"
                    value={values[filter.name] ?? ""}
                    aria-label={filter.allLabel}
                    onChange={(e) => onChange?.(filter.name, e.target.value)}
                >
                    <option value="">{filter.allLabel}</option>
                    {filter.options.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            ))}

            {action && <div className="filterbar-action">{action}</div>}
        </div>
        {resultNote && <div className="filterbar-note">{resultNote}</div>}
    </div>
);

/** Case-insensitive match of a search term against the given fields. */
export const matches = (term: string, ...fields: Array<string | null | undefined>): boolean => {
    const needle = term.trim().toLowerCase();
    if (!needle) return true;
    return fields.some((f) => (f ?? "").toLowerCase().includes(needle));
};
